import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Tag,
  Input,
  Form,
  Row,
  Col,
  Select,
  Radio,
  Affix,
  message,
  Alert,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useSelector } from 'react-redux';
import { addTurnPortalApi, updateTurnPortalApi } from '../../../api/turn';
import { getSocialWorksApi } from '../../../api/patient';
import './NewTurnForm.scss';
import { countryCodes } from '../../../utils/constants';
import { getServicesApi } from '../../../api/service';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CustomLoading from '../../CustomLoading/CustomLoading';
import PatientNumberField from '../../PatientNumberField/PatientNumberField';
import { usePortalRecaptcha } from '../../../providers/RecaptchaProvider';
import {
  patientFacingTurnError,
  runTurnWithCaptchaRetry,
  withTurnSecurity,
} from '../../../utils/portalTurnSecurity';

import { useHistory } from 'react-router-dom';

dayjs.extend(utc);
dayjs.extend(timezone);

// Países donde se usa código de área (ej. Argentina, Uruguay). El resto: país + número directo (ej. Bolivia, Perú).
const COUNTRY_CODES_WITH_AREA = ['+54', '+598'];

export default function NewTurnForm({
  date,
  doctor,
  scheduleConfig,
  turnId,
  setReload,
  turnUser,
  turnType,
  hourStart,
  setIsVisibleModal,
  size,
  turns,
  setTurnDetail,
  selectedBranch,
  preselectedServiceId,
  initialValues,
  onDraftChange,
}) {
  const [loading, setLoading] = useState(false);
  const [socialWorks, setSocialWorks] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [selectedService, setSelectedService] = useState(
    preselectedServiceId || null,
  );
  const [turnsSocialWorks, setTurnsSocialWorks] = useState([]);

  const {
    tenantId,
    tenantTimezone,
    countryCode,
    direction,
    organizationPhone,
  } = useSelector((store) => store.tenantData);
  const [patientForm] = Form.useForm();
  const history = useHistory();
  const { executeRecaptcha } = usePortalRecaptcha();

  useEffect(() => {
    if (!initialValues) return;
    // En el portal este form se monta/desmonta al abrir/cerrar modal.
    // Seteamos valores iniciales para "recordar" datos al reintentar.
    patientForm.setFieldsValue(initialValues);
  }, [initialValues, patientForm]);

  useEffect(() => {
    const getData = () => {
      getSocialWorksApi(tenantId)
        .then((response) => {
          setSocialWorks(response?.socialWorks);
        })
        .catch((err) => console.log(err));

      getServicesApi(tenantId)
        .then((response) => {
          setServicesData(response?.services);
        })
        .catch((err) => console.log(err));
    };

    getData();
  }, []);

  useEffect(() => {
    // Crear un mapa para contar los turnos por obra social
    const socialWorkCount = turns?.reduce((acc, turn) => {
      // Solo contar turnos que no estén eliminados (softDelete: false)
      if (!turn.softDelete) {
        // Incrementar el contador para la obra social
        acc[turn.socialWork] = (acc[turn.socialWork] || 0) + 1;

        // Incrementar el contador total
        acc.total =
          dayjs(hourStart).tz(tenantTimezone).unix() >
          dayjs().tz(tenantTimezone).add(2, 'hours').unix()
            ? (acc.total || 0) + 1
            : acc.total || 0;
      }
      return acc;
    }, {});

    setTurnsSocialWorks(socialWorkCount || { total: 0 });
  }, [turns, date, hourStart, tenantTimezone]);

  // Sincronizar selectedService con el valor del formulario o con el servicio preseleccionado
  useEffect(() => {
    if (preselectedServiceId) {
      setSelectedService(preselectedServiceId);
      patientForm.setFieldsValue({ studiesTypes: preselectedServiceId });
    } else {
      const formValue = patientForm.getFieldValue('studiesTypes');
      if (formValue) setSelectedService(formValue);
    }
  }, [patientForm, preselectedServiceId]);

  const onFinish = async (values) => {
    setLoading(true);
    message.loading('Solicitando turno, aguarde un momento...', 0);

    values.socialWork = values.socialWork === false ? '' : values.socialWork;
    if (preselectedServiceId) values.studiesTypes = preselectedServiceId;

    const useAreaCode = COUNTRY_CODES_WITH_AREA.includes(values?.countryCode);
    let notificationPhone = {
      countryCode: values?.countryCode,
      regionCode: useAreaCode ? values?.regionCode : undefined,
      number: values?.number,
    };

    const serviceId = preselectedServiceId || values?.studiesTypes;
    let services = servicesData?.filter((service) => {
      if (serviceId === service._id) {
        return { service: service.name, price: service?.price };
      }
      return null;
    });

    let price =
      services?.reduce(
        (accumulator, currentValue) =>
          (accumulator || 0) + parseFloat(currentValue.price || 0),
        0,
      ) || null;

    const params = {
      turnDetail: {
        hour: dayjs(hourStart).tz(tenantTimezone),
        services: services || null,
        dni: values.dni,
      },
      doctor,
      turnType,
      description:
        scheduleConfig?.descriptionVisible && scheduleConfig?.description,
      dni: values.dni,
    };

    try {
      const result = await runTurnWithCaptchaRetry({
        patientNumber: values.patientNumber,
        executeRecaptcha,
        action: turnId ? 'update_turn' : 'add_turn',
        send: (security) =>
          turnId
            ? updateTurnPortalApi({
                tenantId,
                turnId,
                dataTurn: withTurnSecurity(
                  {
                    hourStart,
                    socialWork: values.socialWork,
                    name: values.name,
                    dni: values.dni,
                    notificationPhone,
                    email: values.email,
                    price,
                    portal: true,
                    studies: services,
                    description: values?.description,
                    turnType,
                    user: turnUser,
                    branchId: selectedBranch?._id || selectedBranch?.id,
                    sizeSlot: size,
                  },
                  security,
                ),
              })
            : addTurnPortalApi({
                tenantId,
                data: withTurnSecurity(
                  {
                    date: dayjs(date).format('YYYY-MM-DD'),
                    user: turnUser,
                    sizeSlot: size,
                    turnType,
                    dataTurn: {
                      hourStart,
                      socialWork: values.socialWork,
                      name: values.name,
                      dni: values.dni,
                      notificationPhone,
                      email: values.email,
                      price,
                      portal: true,
                      studies: services,
                      description: values?.description,
                      branchId: selectedBranch?._id || selectedBranch?.id,
                    },
                  },
                  security,
                ),
              }),
      });

      if (result.blocked) {
        message.destroy();
        message.warning(result.message, 4);
        setLoading(false);
        return;
      }

      const response = result.response;
      message.destroy();

      if (response?.code !== 200) {
        await message.warning(patientFacingTurnError(response), 5);
        if (typeof setReload === 'function') setReload(true);
      } else {
        await message.success(response.message, 1);
        if (response?.voucherIncomplete) {
          message.warning(
            'El turno se guardó, pero no se pudo generar el código para cancelar.',
            6,
          );
        }
        const bookedAtUnix = Math.floor(Date.now() / 1000);
        params.cancelLink = response?.cancelLink;
        params.direction = response?.direction || direction;
        params.phone = response?.phone || organizationPhone;
        params.bookedAtUnix = bookedAtUnix;
        params.turnDetail.bookedAtUnix = bookedAtUnix;
        params.voucherIncomplete = Boolean(response?.voucherIncomplete);

        setTurnDetail
          ? setTurnDetail({
              hour: dayjs(hourStart).tz(tenantTimezone),
              services: services || null,
              cancelLink: response?.cancelLink,
              direction: response?.direction || direction,
              phone: response?.phone || organizationPhone,
              dni: values.dni,
              bookedAtUnix,
            })
          : history.push(`/confirm-turn`, params);
      }
    } catch (error) {
      console.error(error);
      message.error('Error al solicitar el turno, intentá nuevamente.');
    }
    message.destroy();

    // Cerrar el modal siempre: si está ocupado, el paciente elige otro horario
    setIsVisibleModal(false);
    setLoading(false);
  };

  const filterSocialWorks = (sws, sc) => {
    const base = (sws || []).filter(
      (sw) => sw.name?.toLowerCase() !== 'particular',
    );

    const day = dayjs(date).locale('es').format('dd');

    if (
      sc?.particularOnlyDays?.length > 0 &&
      sc.particularOnlyDays.includes(day)
    ) {
      return [];
    }

    if (!sc?.coverageType || sc?.coverageType === 'acceptAll') {
      return base;
    }

    const configMap = (sc.socialWorksConfig || []).reduce((map, cfg) => {
      if (!map[cfg.socialWork]) map[cfg.socialWork] = [];
      map[cfg.socialWork].push(cfg);
      return map;
    }, {});

    const dataFilter = base.map((sw) => {
      const cfgs = configMap[sw.name] || false;

      if (cfgs && cfgs.every((c) => c.exclusion === true)) {
        return { ...sw, disabled: true, excluded: true, available: false };
      }

      const filteredConfig =
        cfgs?.length > 0 ? cfgs.find((c) => c?.days?.includes(day)) : false;
      const isEnabled = cfgs ? !!filteredConfig?.days?.includes(day) : true;
      const available =
        filteredConfig?.quantity != null
          ? filteredConfig.quantity - (turnsSocialWorks[sw.name] || 0)
          : false;

      return {
        ...sw,
        disabled: !isEnabled,
        excluded: false,
        daysForWork:
          filteredConfig === undefined
            ? { days: Array.from(new Set(cfgs?.flatMap((c) => c.days))) }
            : filteredConfig,
        available,
      };
    });

    if (sc?.coverageType === 'onlyListed') {
      return dataFilter.filter((sw) =>
        (sc.socialWorksConfig || []).some((c) => c.socialWork === sw.name),
      );
    }

    return dataFilter;
  };

  const filteredSocialWorks = useMemo(
    () => filterSocialWorks(socialWorks, scheduleConfig),
    [socialWorks, scheduleConfig, turnsSocialWorks, date],
  );

  const onlyParticularDay = useMemo(() => {
    if (scheduleConfig?.particularOnlyDays?.length > 0 && date) {
      const dayCode = dayjs(date).locale('es').format('dd');
      if (scheduleConfig.particularOnlyDays.includes(dayCode)) return true;
    }
    if (
      !scheduleConfig?.coverageType ||
      scheduleConfig?.coverageType === 'acceptAll'
    ) {
      return false;
    }
    if (filteredSocialWorks.length === 0) return true;
    return filteredSocialWorks.every(
      (sw) =>
        sw.disabled || (typeof sw.available === 'number' && sw.available <= 0),
    );
  }, [
    filteredSocialWorks,
    scheduleConfig?.coverageType,
    scheduleConfig?.particularOnlyDays,
    date,
  ]);

  useEffect(() => {
    patientForm.setFieldsValue({
      socialWork: onlyParticularDay ? 'PARTICULAR' : null,
    });
  }, [onlyParticularDay, turns, date, turnUser, patientForm]);

  if (loading) {
    return <CustomLoading loading={loading} />;
  }

  return (
    <div>
      <Form
        onFinish={onFinish}
        form={patientForm}
        onValuesChange={(_, allValues) => {
          if (typeof onDraftChange === 'function') onDraftChange(allValues);
        }}
      >
        <Row gutter={24}>
          <Col xs={24} md={8} lg={8}>
            <Form.Item
              name="dni"
              getValueFromEvent={(event) =>
                String(event?.target?.value ?? '').replace(/\D/g, '')
              }
              rules={[
                {
                  validator: (_, value) => {
                    const text = String(value ?? '');
                    if (!text || !/^\d+$/.test(text)) {
                      return Promise.reject(new Error('Ingresá el DNI sin puntos'));
                    }
                    if (text.length < 5) {
                      return Promise.reject(new Error('DNI inválido'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              label={
                countryCode === '+54' ? 'DNI' : 'DNI / Cedula de Identidad'
              }
              labelCol={{ span: 24 }}
            >
              <Input
                className="portal-dni-input"
                placeholder="34256389"
                type="number"
                inputMode="numeric"
                onKeyDown={(event) => {
                  if (['.', ',', 'e', 'E', '+', '-'].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onBeforeInput={(event) => {
                  if (event.data && /\D/.test(event.data)) {
                    event.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>
          <PatientNumberField />
          <Col xs={24} md={16} lg={16}>
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Debe cargar el Apellido y Nombre',
                },
              ]}
              label="Apellido y Nombre"
              labelCol={{ span: 24 }}
            >
              <Input
                placeholder="Apellido Nombre"
                style={{ textTransform: 'capitalize' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} lg={8}>
            {onlyParticularDay && (
              <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Este día solo se atiende de forma particular"
                description="No hay cobertura disponible para este horario."
                style={{ marginBottom: 12 }}
              />
            )}
            <Form.Item
              name="socialWork"
              rules={[
                { required: true, message: 'Debe cargar la obra social' },
              ]}
              label="Obra social / Seguro"
              labelCol={{ span: 24 }}
            >
              {onlyParticularDay ? (
                <Select disabled>
                  <Select.Option value="PARTICULAR">
                    <span style={{ textTransform: 'uppercase' }}>Particular</span>
                  </Select.Option>
                </Select>
              ) : (
                <Select
                  placeholder="Obra Social"
                  showSearch
                  optionFilterProp="value"
                >
                  {filteredSocialWorks.map((filteredSocialWork) => (
                    <Select.Option
                      key={filteredSocialWork.name}
                      value={filteredSocialWork.name}
                      disabled={
                        filteredSocialWork?.disabled ||
                        (typeof filteredSocialWork?.available === 'number' &&
                          filteredSocialWork?.available <= 0)
                      }
                    >
                      <span style={{ textTransform: 'uppercase' }}>
                        {filteredSocialWork.name}
                      </span>

                      {(() => {
                        if (filteredSocialWork?.excluded) {
                          return (
                            <Tag color="default" style={{ marginLeft: '8px' }}>
                              Sin convenio
                            </Tag>
                          );
                        }

                        if (filteredSocialWork?.disabled) {
                          return ` (Atiende los: ${filteredSocialWork?.daysForWork?.days.join(
                            ', ',
                          )})`;
                        }

                        if (
                          typeof filteredSocialWork?.available === 'number' &&
                          filteredSocialWork?.available <= 0
                        ) {
                          return (
                            <Tag color="red" style={{ marginLeft: '8px' }}>
                              Sin cupo - Atiende los:{' '}
                              {filteredSocialWork?.daysForWork?.days.join(', ')}
                            </Tag>
                          );
                        }

                        return (
                          <Tag
                            color={
                              filteredSocialWork?.available > 5 ||
                              !filteredSocialWork?.available
                                ? 'success'
                                : filteredSocialWork?.available >= 3
                                  ? 'gold'
                                  : 'volcano'
                            }
                            style={{ marginLeft: '8px' }}
                          >
                            {filteredSocialWork?.available > 5 ||
                            !filteredSocialWork?.available
                              ? 'Disponible'
                              : `Disponibles: ${filteredSocialWork?.available}`}
                          </Tag>
                        );
                      })()}
                    </Select.Option>
                  ))}
                  <Select.Option value="PARTICULAR">
                    <span style={{ textTransform: 'uppercase' }}>
                      Particular
                    </span>
                  </Select.Option>
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col xs={24} md={8} lg={8}>
            <Form.Item
              name="email"
              label="Correo electronico"
              labelCol={{ span: 24 }}
              rules={[
                {
                  required: true,
                  type: 'email',
                  message:
                    'Debe cargar un mail valido. (sin espacios al final)',
                },
              ]}
              tooltip="En caso de que su turno sea cancelado recibira una notificación via mail"
            >
              <Input placeholder="Correo electronico" inputMode="email" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} lg={8}>
            <Form.Item label="Teléfono de contacto" labelCol={{ span: 24 }}>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.countryCode !== curr.countryCode
                }
              >
                {({ getFieldValue }) => {
                  const selectedCountry =
                    getFieldValue('countryCode') || countryCode;
                  const useAreaCode =
                    COUNTRY_CODES_WITH_AREA.includes(selectedCountry);
                  return (
                    <Row>
                      <Col xs={12} xl={useAreaCode ? 6 : 8}>
                        <Form.Item
                          name="countryCode"
                          noStyle
                          initialValue={countryCode}
                        >
                          <Select
                            style={{ fontSize: '8pt' }}
                            onChange={() => {
                              patientForm.setFieldsValue({
                                regionCode: undefined,
                              });
                            }}
                          >
                            {countryCodes?.map((country) => (
                              <Select.Option value={country.dial_code}>
                                {country.emoji}
                                {country.dial_code}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      {useAreaCode && (
                        <Col xs={12} xl={6}>
                          <Form.Item
                            name="regionCode"
                            noStyle
                            rules={[
                              {
                                validator: (_, value) => {
                                  const code = String(value ?? '').trim();
                                  if (!code) {
                                    return Promise.reject(
                                      new Error(
                                        'Debe completar el código de área. Ej: 385',
                                      ),
                                    );
                                  }
                                  if (!/^\d{2,5}$/.test(code)) {
                                    return Promise.reject(
                                      new Error(
                                        'Código de área inválido (solo números, 2 a 5 cifras)',
                                      ),
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <Input
                              placeholder="Cod Area"
                              maxLength={5}
                              type="tel"
                              inputMode="numeric"
                              autoComplete="tel-area-code"
                            />
                          </Form.Item>
                        </Col>
                      )}
                      <Col xs={24} xl={useAreaCode ? 12 : 16}>
                        <Form.Item
                          name="number"
                          noStyle
                          rules={[
                            {
                              validator: (_, value) => {
                                const num = String(value ?? '')
                                  .trim()
                                  .replace(/\s/g, '');
                                if (!num) {
                                  return Promise.reject(
                                    new Error(
                                      useAreaCode
                                        ? 'Debe completar el número. Ej: 4889915'
                                        : 'Debe completar el número de WhatsApp (solo números)',
                                    ),
                                  );
                                }
                                if (!/^\d{6,15}$/.test(num)) {
                                  return Promise.reject(
                                    new Error(
                                      'Número inválido (solo números, 6 a 15 cifras)',
                                    ),
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            addonBefore={
                              selectedCountry === '+54' ? '15' : undefined
                            }
                            placeholder={
                              useAreaCode ? 'Nro Tel.' : 'Ej: 71234567'
                            }
                            maxLength={
                              useAreaCode && selectedCountry === '+54' ? 8 : 15
                            }
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel-national"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </Col>
          {(() => {
            // Si el servicio viene preseleccionado (portal: servicios por bloque), mostrar solo el nombre
            if (preselectedServiceId) {
              const serviceName = servicesData?.find(
                (s) => (s._id || s.id) === preselectedServiceId,
              )?.name;
              return (
                <Col xs={24}>
                  {serviceName && (
                    <p style={{ marginBottom: 16, color: '#666' }}>
                      <strong>Servicio:</strong> {serviceName}
                    </p>
                  )}
                </Col>
              );
            }

            // Verificar si hay servicios públicos disponibles (modo global o sin preselección)
            const serviceIds =
              scheduleConfig?.serviceConfigMode === 'perBlock'
                ? scheduleConfig?.hours?.flatMap((h) => h.serviceIds || []) ||
                  []
                : scheduleConfig?.service || [];
            const availablePublicServices = servicesData?.filter(
              (service) =>
                service?.public && serviceIds.some((id) => id === service._id),
            );

            // Solo mostrar el campo si hay servicios públicos disponibles
            if (availablePublicServices?.length > 0) {
              return (
                <Col xs={24}>
                  <Form.Item
                    name="studiesTypes"
                    label="Servicios"
                    labelCol={{ span: 24 }}
                    rules={[
                      {
                        required: true,
                        message: 'Debe seleccionar al menos un servicio',
                      },
                    ]}
                  >
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Seleccione servicios.. (*)"
                      showSearch={false}
                      inputMode="none"
                      value={patientForm.getFieldValue('studiesTypes')}
                      onChange={(value) => {
                        patientForm.setFieldsValue({ studiesTypes: value });
                        setSelectedService(value);
                      }}
                    >
                      {availablePublicServices?.map((service) => (
                        <Select.Option
                          key={service?._id}
                          value={service?._id}
                          title={`${service?.name} - ${service?.description}`}
                        >
                          {`${service?.name} ${service?.price ? ' - ' : ''} ${
                            service?.price?.toLocaleString('es-AR', {
                              style: 'currency',
                              currency: 'ARS',
                              minimumFractionDigits: 0,
                            }) || ''
                          }`}
                        </Select.Option>
                      ))}
                    </Select>

                    {/* Información detallada del servicio seleccionado */}
                    {selectedService && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: 16,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 8,
                          border: '1px solid #d9d9d9',
                        }}
                      >
                        <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>
                          {
                            servicesData?.find((s) => s._id === selectedService)
                              ?.name
                          }
                        </h4>
                        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
                          Valor:{' '}
                          {servicesData
                            ?.find((s) => s._id === selectedService)
                            ?.price?.toLocaleString('es-AR', {
                              style: 'currency',
                              currency: 'ARS',
                              minimumFractionDigits: 0,
                            })}
                        </p>
                        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
                          {
                            servicesData?.find((s) => s._id === selectedService)
                              ?.description
                          }
                        </p>
                      </div>
                    )}
                  </Form.Item>
                </Col>
              );
            }

            // Si no hay servicios públicos, mostrar el campo de descripción para turnos médicos
            if (turnType === 'medico') {
              return (
                <Col xs={24} md={8} lg={8}>
                  <Form.Item
                    name="description"
                    label="Tipo de turno (opcional)"
                    labelCol={{ span: 24 }}
                  >
                    <Radio.Group>
                      <Radio value="Primera Vez">Primera Vez</Radio>
                      <Radio value="Consulta">Consulta</Radio>
                      <Radio value="Consulta + Practica">
                        Consulta + Practica
                      </Radio>{' '}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              );
            }

            return null;
          })()}
        </Row>

        <Row gutter={24} style={{ textAlign: 'center' }}>
          <Col span={24}>
            <Form.Item>
              <Affix offsetBottom={10}>
                <Button
                  type="default"
                  htmlType="submit"
                  style={{
                    width: '100%',
                  }}
                  disabled={loading}
                  loading={loading}
                >
                  Solicitar turno
                </Button>
              </Affix>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
